import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../utils/api';
import { classNames } from '../utils/classNames';

interface Shop {
  id: number;
  name: string;
  api_key: string;
  currency: string;
}

interface ShopAdmin {
  id: number;
  username: string;
  is_active: boolean;
  created_at: string;
}

interface CreateAdminData {
  username: string;
  password: string;
}

const ShopAdminManagement: React.FC = () => {
  const { user } = useAuth();
  const [shops, setShops] = useState<Shop[]>([]);
  const [selectedShop, setSelectedShop] = useState<number | null>(null);
  const [admins, setAdmins] = useState<ShopAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createForm, setCreateForm] = useState<CreateAdminData>({
    username: '',
    password: '',
  });

  useEffect(() => {
    if (user?.role === 'owner') {
      fetchShops();
    }
  }, [user]);

  useEffect(() => {
    if (selectedShop) {
      fetchAdmins(selectedShop);
    }
  }, [selectedShop]);

  const fetchShops = async () => {
    try {
      const { data } = await api.get('/owner/shops');
      setShops(data);
      if (data.length > 0 && !selectedShop) {
        setSelectedShop(data[0].id);
      }
    } catch (error) {
      console.error('Error fetching shops:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAdmins = async (shopId: number) => {
    try {
      const { data } = await api.get(`/owner/shops/${shopId}/admins`);
      setAdmins(data);
    } catch (error: any) {
      console.error('Error fetching admins:', error);
      if (error?.response?.status === 401) {
        alert('Сессия истекла. Пожалуйста, войдите в систему заново.');
      }
    }
  };

  const handleCreateAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedShop || !user) return;

    if (user.role !== 'owner') {
      alert('У вас нет прав для создания администраторов.');
      return;
    }

    try {
      const { data } = await api.post(`/owner/shops/${selectedShop}/admins`, createForm);
      alert(`Администратор ${data.username} успешно создан!`);
      setCreateForm({ username: '', password: '' });
      setShowCreateForm(false);
      fetchAdmins(selectedShop);
    } catch (error: any) {
      console.error('Error creating admin:', error);
      const msg = error?.response?.data?.error || 'Ошибка при создании администратора';
      alert(`Ошибка: ${msg}`);
    }
  };

  const handleToggleAdminStatus = async (adminId: number, isActive: boolean) => {
    if (!selectedShop) return;
    try {
      await api.put(`/owner/shops/${selectedShop}/admins/${adminId}/status`, { isActive: !isActive });
      fetchAdmins(selectedShop);
      alert('Статус администратора обновлен!');
    } catch (error: any) {
      const msg = error?.response?.data?.error || 'Ошибка при обновлении статуса';
      alert(msg);
    }
  };

  const handleResetPassword = async (adminId: number) => {
    if (!selectedShop) return;
    const newPassword = prompt('Введите новый пароль для администратора:');
    if (!newPassword) return;
    try {
      await api.post(`/owner/shops/${selectedShop}/admins/${adminId}/reset-password`, { newPassword });
      alert('Пароль успешно изменен');
    } catch (error: any) {
      const msg = error?.response?.data?.error || 'Ошибка при сбросе пароля';
      alert(msg);
    }
  };

  const handleDeleteAdmin = async (adminId: number) => {
    if (!selectedShop) return;
    if (!confirm('Вы уверены, что хотите удалить этого администратора?')) return;
    try {
      await api.delete(`/owner/shops/${selectedShop}/admins/${adminId}`);
      await fetchAdmins(selectedShop);
      alert('Администратор удален');
    } catch (error: any) {
      const msg = error?.response?.data?.error || 'Ошибка при удалении администратора';
      alert(msg);
    }
  };

  const getCurrencySymbol = (currency: string) => {
    const symbols: Record<string, string> = {
      RUB: '₽',
      KZT: '₸',
      USD: '$',
      EUR: '€',
    };
    return symbols[currency] || currency;
  };

  const selectedShopData = useMemo(() => shops.find((shop) => shop.id === selectedShop) || null, [shops, selectedShop]);

  if (user?.role !== 'owner') {
    return <div className="flex min-h-[40vh] items-center justify-center text-danger">Доступ запрещен</div>;
  }

  if (loading) {
    return <div className="flex min-h-[40vh] items-center justify-center text-[var(--app-text-muted)]">Загрузка…</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="space-y-1">
          <h2 className="text-xl font-semibold text-[var(--app-heading)]">Управление администраторами магазинов</h2>
          <p className="text-sm text-[var(--app-text-muted)]">Назначайте и управляйте администраторами для каждого магазина.</p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          disabled={!selectedShop}
          className={classNames(
            'inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--app-ring-offset)]',
            selectedShop
              ? 'bg-primary-500 text-white hover:bg-primary-600'
              : 'cursor-not-allowed bg-[var(--app-surface-subtle)] text-[var(--app-text-muted)]'
          )}
        >
          Добавить администратора
        </button>
      </div>

      <div className="card">
        <div className="card-body space-y-3">
          <label className="space-y-2 text-sm font-medium text-[var(--app-text)]">
            <span>Выберите магазин</span>
            <select
              value={selectedShop || ''}
              onChange={(e) => setSelectedShop(Number(e.target.value) || null)}
              className="w-full rounded-2xl border border-[var(--app-border)] bg-[var(--app-input-bg)] px-3 py-2 text-sm text-[var(--app-text)] focus:border-primary-400 focus:outline-none focus:ring-4 focus:ring-primary-500/20"
            >
              <option value="">Выберите магазин...</option>
              {shops.map((shop) => (
                <option key={shop.id} value={shop.id}>
                  {shop.name} (ID: {shop.id}) — {shop.currency} {getCurrencySymbol(shop.currency)}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {selectedShopData ? (
        <div className="space-y-6">
          <div className="card">
            <div className="card-body space-y-3">
              <h3 className="text-lg font-semibold text-[var(--app-heading)]">{selectedShopData.name}</h3>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1 text-sm text-[var(--app-text-muted)]">
                  <p className="font-semibold text-[var(--app-text)]">API Key:</p>
                  <code className="inline-flex break-all rounded-xl border border-[var(--app-border)] bg-[var(--app-surface-subtle)] px-3 py-2 text-xs text-[var(--app-text)]">
                    {selectedShopData.api_key}
                  </code>
                </div>
                <div className="space-y-1 text-sm text-[var(--app-text-muted)]">
                  <p className="font-semibold text-[var(--app-text)]">Валюта</p>
                  <p>
                    {selectedShopData.currency} {getCurrencySymbol(selectedShopData.currency)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-semibold text-[var(--app-heading)]">Администраторы магазина</h3>
            </div>
            <div className="card-body px-0 py-0">
              {admins.length === 0 ? (
                <div className="px-6 py-10 text-center text-sm text-[var(--app-text-muted)]">Администраторы не найдены</div>
              ) : (
                <ul className="divide-y divide-[var(--app-border)]">
                  {admins.map((admin) => (
                    <li key={admin.id} className="px-6 py-4">
                      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                        <div className="space-y-1">
                          <h4 className="text-lg font-semibold text-[var(--app-heading)]">{admin.username}</h4>
                          <p className="text-sm text-[var(--app-text-muted)]">
                            Создан: {new Date(admin.created_at).toLocaleDateString('ru-RU')}
                          </p>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <span
                            className={classNames(
                              'inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold',
                              admin.is_active
                                ? 'bg-success/15 text-success'
                                : 'bg-danger/15 text-danger'
                            )}
                          >
                            {admin.is_active ? 'Активен' : 'Неактивен'}
                          </span>
                          <button
                            onClick={() => handleToggleAdminStatus(admin.id, admin.is_active)}
                            className={classNames(
                              'inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--app-ring-offset)]',
                              admin.is_active
                                ? 'bg-danger/10 text-danger hover:bg-danger/15'
                                : 'bg-success/10 text-success hover:bg-success/20'
                            )}
                          >
                            {admin.is_active ? 'Деактивировать' : 'Активировать'}
                          </button>
                          <button
                            onClick={() => handleResetPassword(admin.id)}
                            className="inline-flex items-center rounded-full bg-primary-50 px-3 py-1 text-xs font-semibold text-primary-600 transition hover:bg-primary-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--app-ring-offset)]"
                          >
                            Сброс пароля
                          </button>
                          <button
                            onClick={() => handleDeleteAdmin(admin.id)}
                            className="inline-flex items-center rounded-full bg-danger/10 px-3 py-1 text-xs font-semibold text-danger transition hover:bg-danger/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-danger/30 focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--app-ring-offset)]"
                          >
                            Удалить
                          </button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {showCreateForm && selectedShopData ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[var(--app-bg)]/80 backdrop-blur-sm">
          <div className="w-full max-w-lg space-y-4 rounded-3xl border border-[var(--app-border)] bg-[var(--app-surface)] p-6 shadow-lifted">
            <div className="space-y-1">
              <h3 className="text-lg font-semibold text-[var(--app-heading)]">
                Создать администратора для "{selectedShopData.name}"
              </h3>
              <p className="text-sm text-[var(--app-text-muted)]">
                Валюта магазина: {selectedShopData.currency} {getCurrencySymbol(selectedShopData.currency)}
              </p>
            </div>
            <form onSubmit={handleCreateAdmin} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-[var(--app-text)]" htmlFor="admin-username">
                  Логин
                </label>
                <input
                  id="admin-username"
                  type="text"
                  value={createForm.username}
                  onChange={(e) => setCreateForm((prev) => ({ ...prev, username: e.target.value }))}
                  className="block w-full rounded-2xl border border-[var(--app-border)] bg-[var(--app-input-bg)] px-4 py-2 text-sm text-[var(--app-text)] focus:border-primary-400 focus:outline-none focus:ring-4 focus:ring-primary-500/20"
                  required
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-[var(--app-text)]" htmlFor="admin-password">
                  Пароль
                </label>
                <input
                  id="admin-password"
                  type="password"
                  value={createForm.password}
                  onChange={(e) => setCreateForm((prev) => ({ ...prev, password: e.target.value }))}
                  className="block w-full rounded-2xl border border-[var(--app-border)] bg-[var(--app-input-bg)] px-4 py-2 text-sm text-[var(--app-text)] focus:border-primary-400 focus:outline-none focus:ring-4 focus:ring-primary-500/20"
                  required
                />
              </div>

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="inline-flex items-center rounded-full bg-[var(--app-surface-subtle)] px-4 py-2 text-sm font-semibold text-[var(--app-text-muted)] transition hover:text-[var(--app-text)]"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  className="inline-flex items-center rounded-full bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--app-ring-offset)]"
                >
                  Создать
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ShopAdminManagement;
